/**
 * PATH: packages/kanban-host/src/allocation/AllocateChipLists.tsx
 * FILE_ID: KANBANHOST-ALLOCATECHIP-LISTS-001
 * PURPOSE: Board/lane picker + mode toggle leaves for AllocateChip.
 * WHY: Split out of AllocateChip.tsx to stay under the 500-line ceiling
 *      (opened while fixing inbox scroll-on-allocate).
 */

import { useMemo, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Star,
} from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'
import type { KanbanApi, Lane } from '@quotech/ui/kanban'
import { getLaneColor } from '@quotech/ui/kanban'

import {
  useAllocateToKanbanMenuData,
  useKanbanBoardLanes,
  type AllocateMenuBoard,
} from '../index'
import {
  filterBoardsByQuery,
  flattenAllocatableBoards,
} from './MoveToBoardPicker'
import { NewLaneInlineRow } from './AllocateToKanbanSubMenu'

export type AllocateMode = 'new' | 'existing'

export function AllocateModeToggle({
  mode,
  onChange,
}: {
  mode: AllocateMode
  onChange: (next: AllocateMode) => void
}) {
  const tab = (value: AllocateMode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === value}
      onClick={() => onChange(value)}
      className={cn(
        'flex-1 rounded px-2 py-1 text-[10px] font-medium transition-colors',
        mode === value
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
      data-testid={`allocate-mode-${value}`}
    >
      {label}
    </button>
  )
  return (
    <div
      role="tablist"
      aria-label="Allocate mode"
      className="mx-1.5 mt-1 mb-1 flex items-center gap-0.5 rounded-md bg-muted/60 p-0.5"
    >
      {tab('new', 'New card')}
      {tab('existing', 'Existing card')}
    </div>
  )
}

export function BoardPickList({
  api,
  orgId,
  busy,
  onPick,
  onCreateBoard,
}: {
  api: KanbanApi
  orgId: number | null
  busy: boolean
  onPick: (board: AllocateMenuBoard) => void
  onCreateBoard?: () => void
}) {
  const [query, setQuery] = useState('')
  const menu = useAllocateToKanbanMenuData({ api, orgId })
  const boards = useMemo(
    () => flattenAllocatableBoards(menu, null),
    [menu],
  )
  const filtered = useMemo(
    () => filterBoardsByQuery(boards, query),
    [boards, query],
  )
  const showLoading = menu.isLoading && menu.totalCount === 0

  return (
    <div className="flex flex-col">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
        Allocate to board
      </div>
      <div className="px-1.5 pb-1">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/60"
            aria-hidden
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search boards…"
            autoFocus
            className="w-full rounded border border-border bg-background pl-6 pr-2 py-1 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label="Search boards"
          />
        </div>
      </div>
      <div className="max-h-[220px] overflow-y-auto">
        {showLoading && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            Loading boards…
          </div>
        )}
        {menu.isError && (
          <div className="px-2 py-1 text-[11px] text-red-500">
            Could not load boards.
          </div>
        )}
        {!showLoading && !menu.isError && boards.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            {onCreateBoard
              ? 'No boards yet — create one below.'
              : 'No boards yet — create one in Kanban first.'}
          </div>
        )}
        {!showLoading && !menu.isError && boards.length > 0 && filtered.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            No boards match “{query.trim()}”.
          </div>
        )}
        {filtered.map((b) => (
          <button
            key={b.id}
            type="button"
            disabled={busy}
            onClick={() => onPick(b)}
            title={`Pick a lane on ${b.name}`}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors',
              'hover:bg-accent disabled:opacity-50',
            )}
          >
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: b.swatch || '#94a3b8' }}
              aria-hidden
            />
            <span className="flex-1 truncate">{b.name}</span>
            <ChevronRight
              className="h-3 w-3 text-muted-foreground/50 shrink-0"
              aria-hidden
            />
          </button>
        ))}
      </div>
      {onCreateBoard && (
        <div className="border-t border-border mt-1 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={onCreateBoard}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1.5 text-[11px] font-medium text-left rounded transition-colors',
              'text-foreground hover:bg-accent disabled:opacity-50',
            )}
            data-testid="allocate-create-board"
          >
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
              <Plus className="h-3 w-3" aria-hidden />
            </span>
            <span className="flex-1 truncate">New board…</span>
          </button>
        </div>
      )}
    </div>
  )
}

export function LanePickList({
  api,
  board,
  busy,
  onBack,
  onPick,
}: {
  api: KanbanApi
  board: AllocateMenuBoard
  busy: boolean
  onBack: () => void
  onPick: (lane: Lane) => void
}) {
  const lanes = useKanbanBoardLanes({ api, boardId: board.id, enabled: true })
  const sorted = useMemo(
    () =>
      lanes.lanes
        .filter((l) => !l.archived)
        .slice()
        .sort((a, b) => a.position - b.position),
    [lanes.lanes],
  )
  const nextPosition = sorted.length
    ? Math.max(...sorted.map((l) => l.position)) + 1
    : 0

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 w-full px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{board.name}</span>
      </button>
      <div className="max-h-[220px] overflow-y-auto">
        {lanes.isLoading && sorted.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            Loading lanes…
          </div>
        )}
        {lanes.isError && (
          <div className="px-2 py-1 text-[11px] text-red-500">
            Could not load lanes.
          </div>
        )}
        {!lanes.isLoading && !lanes.isError && sorted.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            No lanes yet — add the first one below.
          </div>
        )}
        {sorted.map((lane) => (
          <button
            key={lane.id}
            type="button"
            disabled={busy}
            onClick={() => onPick(lane)}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors',
              'hover:bg-accent disabled:opacity-50',
            )}
          >
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: getLaneColor(lane) }}
              aria-hidden
            />
            <span className="flex-1 truncate">{lane.name}</span>
            {typeof lane.card_count === 'number' && (
              <span className="text-[9px] text-muted-foreground/70 shrink-0">
                · {lane.card_count}
              </span>
            )}
            {lane.id === lanes.defaultLaneId && (
              <Star
                className="h-2.5 w-2.5 text-amber-500 shrink-0"
                aria-label="Default lane"
              />
            )}
          </button>
        ))}
      </div>
      <div className="border-t border-border mt-1 pt-1">
        <NewLaneInlineRow
          api={api}
          boardId={board.id}
          nextPosition={nextPosition}
          busy={busy}
          onCreated={(laneId) => {
            const created = lanes.lanes.find((row) => row.id === laneId)
            if (created) {
              onPick(created)
              return
            }
            onPick({
              id: laneId,
              board: board.id,
              name: 'New lane',
              kind: 'BACKLOG',
              position: nextPosition,
              description: '',
              color: '',
              wip_limit: 0,
              action_template_ids_json: [],
              classification_prompt_override: '',
              ai_suggested_at: null,
              ai_suggestion_reason: '',
              approved_at: null,
              archived: false,
              triggers: [],
              created_at: '',
              updated_at: '',
            })
          }}
        />
      </div>
    </div>
  )
}
